import { Modal } from "react-bootstrap";
import moment from "moment";
import ReactTooltip from "react-tooltip";
import { getTitleCase } from "@utils/utils";
import { buildFilterGroups, datasetLabel, normalizeDirectionLabel } from "./utils";
import type { SavedSearchRow } from "./types";

type ViewSavedSearchModalProps = {
  row: SavedSearchRow | null;
  onClose: () => void;
};

const pillCls = "tw-flex tw-items-center tw-gap-1 tw-w-fit tw-rounded-full tw-border tw-border-gray-300 tw-px-2.5 tw-py-0.5 tw-text-xs tw-text-gray-900";
const sectionTitleCls = "tw-text-xs tw-font-medium tw-text-gray-600";
const headingCls = "tw-text-sm tw-font-medium tw-text-gray-900";

const ViewSavedSearchModal = ({ row, onClose }: ViewSavedSearchModalProps) => {
  if (!row) return null;

  const category = normalizeDirectionLabel(row.search_category);
  const filterGroups = buildFilterGroups(row.search_data?.filters, row.search_data?.tradeDirection?.value);

  return (
    <Modal show={!!row} onHide={onClose} centered animation={false} dialogClassName="tw-max-w-[620px] tw-w-full" contentClassName="!tw-rounded-[5px] !tw-border-0 !tw-shadow-[0_0_10px_rgba(0,0,0,0.3)] !tw-p-0">
      <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-gray-300 tw-px-3 tw-py-3">
        <span className="tw-text-base tw-font-medium tw-text-gray-900">Saved Search Details</span>
        <span className="material-symbols-outlined tw-cursor-pointer tw-text-gray-900 tw-font-bold" onClick={onClose}>
          close
        </span>
      </div>

      <div className="tw-px-4 tw-py-3">
        <div className="tw-flex tw-items-start tw-justify-between tw-border-b tw-border-gray-200 tw-pb-3">
          <div className="tw-flex tw-flex-col tw-gap-1 tw-max-w-[80%]">
            <span className="tw-text-sm tw-font-medium tw-text-gray-900">{getTitleCase(row.search_name)}</span>
          </div>
          {row.created_at && <span className="tw-text-xs tw-text-gray-500 tw-shrink-0">{moment(row.created_at, "DD MMM, YYYY").isValid() ? moment(row.created_at, "DD MMM, YYYY").format("MMM DD, YYYY") : row.created_at}</span>}
        </div>

        <div className="tw-flex tw-bg-gray-50 tw-rounded-lg tw-my-3">
          <div className="tw-w-1/2 tw-flex tw-flex-col tw-gap-5 tw-p-3">
            <div className={headingCls}>Searched Details</div>
            <div className="tw-flex tw-flex-col tw-gap-1">
              <div className={sectionTitleCls}>Search Category</div>
              <div className="tw-flex tw-flex-wrap tw-gap-3">
                <span className={pillCls}>{category}</span>
              </div>
            </div>
            <div className="tw-flex tw-flex-col tw-gap-1">
              <div className={sectionTitleCls}>Search Phrase</div>
              <div className="tw-flex tw-flex-wrap tw-gap-3">
                {row.search_data?.search?.length ? (
                  row.search_data.search.map((item) => (
                    <span key={item.label} className={pillCls}>
                      {item.label}
                    </span>
                  ))
                ) : (
                  <span className={pillCls}>NA</span>
                )}
              </div>
            </div>
            {datasetLabel(row.search_data?.datasetType) ? (
              <div className="tw-flex tw-flex-col tw-gap-1">
                <div className={sectionTitleCls}>Dataset</div>
                <div className="tw-flex tw-flex-wrap tw-gap-3">
                  <span className={pillCls}>{datasetLabel(row.search_data?.datasetType)}</span>
                </div>
              </div>
            ) : null}
            <div className="tw-flex tw-flex-col tw-gap-2">
              <div className={sectionTitleCls}>Date Range</div>
              <div className="tw-flex tw-flex-wrap tw-gap-3">
                <span className={pillCls}>{row.search_data?.filters?.dateRange?.label || "NA"}</span>
              </div>
            </div>
          </div>
          <div className="tw-w-1/2 tw-flex tw-flex-col tw-gap-5 tw-p-3 tw-border-l tw-border-gray-200">
            <div className={headingCls}>Applied Filters</div>
            {filterGroups.length ? (
              filterGroups.map((group) => (
                <div key={group.label} className="tw-flex tw-flex-col tw-gap-2">
                  <div className={sectionTitleCls}>{group.label}</div>
                  <div className="tw-flex tw-flex-wrap tw-gap-3">
                    {group.pills.map((pill) => (
                      <span key={pill.text} className={pillCls} data-tip={pill.tooltip || ""}>
                        {pill.text}
                      </span>
                    ))}
                    {group.overflowCount ? (
                      <span className={pillCls} data-tip={group.overflowTooltip}>
                        +{group.overflowCount}
                      </span>
                    ) : null}
                  </div>
                </div>
              ))
            ) : (
              <span className="tw-text-sm tw-text-gray-500">No filters have been added</span>
            )}
          </div>
        </div>
      </div>
      <ReactTooltip className="trademo-tooltip" backgroundColor="#8c8c8c" textColor="#FFFFFF" />
    </Modal>
  );
};

export default ViewSavedSearchModal;
